import { Inject } from '@odinjs/odin';

import { ActionAPI, BaseModalProps, ButtonAPI, ContextualBarAPI, DeckAPI, ModalButton, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';

import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { EnteralNutritionViewerAliases, FragmentNames } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import { ActionType, InfusionHourStatus, InfusionItemStatus } from '../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../constants/routes';
import BaseController from '../../../../controllers/BaseController';
import { MedicationAndDateHeaderParams } from '../../../../controllers/commons/header/medication-and-date-header-params';
import { YesNoType } from '../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import ShowToast from '../../../../utils/toast-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { InfusionsViewerParams } from '../../infusion-viewer/infusions-viewer-params';
import { AdminByScheduleParams } from '../administration-schedule/administration-schedule-params';
import { AdminByProcessParams } from '../medication-administration-by-process/medication-administration-by-process-params';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { PrescriptionPosition } from '../oral-supplements-viewer/oral-supplements-type';
import { EnteralNutritionType } from './enteral-nutrition-type';
import TimelineContext, { PrescriptionType } from '../../services/timeline-service';
import { MedicationStatus } from '../administration-schedule/administration-schedule-types';
import { ItemStatus } from '../../../../constants/utils-adep';

@Controller({ component: EnteralNutritionViewerAliases.ENTERAL_NUTRITION_VIEWER_LAYOUT, fragment: FragmentNames.ENTERAL_NUTRITION_VIEWER })
class EnteralNutritionController extends BaseController {
  @Inject(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_VIEWER_DECK)
  private deckApi!: DeckAPI;

  @Inject(EnteralNutritionViewerAliases.EXECUTE_TIME_BUTTON)
  private executeTimeButtonApi!: ButtonAPI;

  @Inject(EnteralNutritionViewerAliases.INTERRUPT_BUTTON)
  private interruptButtonApi!: ButtonAPI;

  @Inject(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  private actionTerminateApi!: ActionAPI;

  @Inject(EnteralNutritionViewerAliases.EXECUTE_TIME_PANEL)
  private executeTimePanelApi!: PanelAPI;

  @Inject(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_CONTENT_PANEL)
  private contentPanelApi!: PanelAPI;

  @Inject(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_FOOTER_PANEL)
  private footerPanelApi!: PanelAPI;

  @Inject(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_VIEWER_CONTEXTUAL_BAR)
  declare enteralNutritionViewerContextualBar: ContextualBarAPI;

  @OnMount(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.fetchEnteralNutrition().then((response) => {

      const enteralNutrition = response as EnteralNutritionType;

      MedicationViewerParams.adminSchedule = {
        dilutionDescription: '',
        materialSequence: enteralNutrition.materialSequence,
        medicationHour: enteralNutrition.scheduleDate,
        prescriptionNumber: enteralNutrition.prescriptionNumber,
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: enteralNutrition.materialDescription,
        scheduleDate: enteralNutrition.scheduleDate,
        type: 'ENTERAL_NUTRITION'
      };

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        amountAdministered: enteralNutrition.doseQuantity,
        cdIntervalId: enteralNutrition.intervalCode,
        id: String(enteralNutrition.scheduleSequence),
        ieViaApplicationDescription: enteralNutrition.routeAdministrationDescription,
        infusionSpeed: String(enteralNutrition.infusionSpeed),
        nrPrescriptionId: String(enteralNutrition.prescriptionNumber),
        partialVolume: enteralNutrition.partialVolume,
        pendingHours: enteralNutrition.pendingHours,
        qtDoseId: String(enteralNutrition.doseQuantity),
        stageVolume: enteralNutrition.stageVolume,
        startId: enteralNutrition.scheduleDate,
        unitMeasureDoseDescription: enteralNutrition.unitMeasureDoseDescription,
      }

      this.context.set({ ...enteralNutrition })

      this.contentPanelApi.setVisible(true)
      this.footerPanelApi.setVisible(true)
      this.checkVisibilityInterruptButton();
    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR)
      }
    })

    this.context.set({
      encounterId,
      patientId,
    });
  }

  @OnClick(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleClickTerminateAction(): void {
    const { patientId, encounterId, hourStatus, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate, itemStatus, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_ENTERAL_NUTRITION_TERMINATE, { currentSpeedInfusion, encounterId, hourStatus, itemStatus, patientId, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate });
  }

  @OnMount(EnteralNutritionViewerAliases.EXECUTE_TIME_BUTTON)
  handleMountInfusionStopButton(): void {
    this.validateAndSetButtonVisibility();
  }

  @OnClick(EnteralNutritionViewerAliases.EXECUTE_TIME_BUTTON)
  handleClickExecuteTimeButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const startSolution = itemStatus === InfusionItemStatus.NOT_STARTED ? 'button.infusion-viewer.start' : 'button.infusion-viewer.restart-solution';
    const scheduleTitleDescription = MessageService.translate(startSolution);
    const enteralNutrition = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { enteralNutrition },
      focusOnOpen: false,
      fragment: FragmentNames.ENTERAL_NUTRITION_START,
      id: FragmentNames.ENTERAL_NUTRITION_START,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.ENTERAL_NUTRITION_START),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };
    AdminByProcessParams.admType = { isAdmProcess: false };

    ModalService.openDefault(dialogModal);
  }

  private checkVisibilityInterruptButton(): void {
    const { pendingHours } = this.context.getBulkData() as unknown as EnteralNutritionType;
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);
    if(isValidItemStatusForInterrupt){
      if (pendingHours && pendingHours > 0) {
        this.executeTimePanelApi.setVisible(true);
        this.interruptButtonApi.setVisible(true);
        return;
      }
      this.executeTimePanelApi.setVisible(false);
      this.interruptButtonApi.setVisible(false);
    }
  }

  @OnMount(EnteralNutritionViewerAliases.INTERRUPT_BUTTON)
  handleMountInterruptButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);
    this.interruptButtonApi.setVisible(isValidItemStatusForInterrupt);
  }

  @OnClick(EnteralNutritionViewerAliases.INTERRUPT_BUTTON)
  handleClickInterruptButton(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    const interruptDescription = MessageService.translate(interruptExpression);
    const enteralNutrition = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { enteralNutrition },
      focusOnOpen: false,
      fragment: FragmentNames.ENTERAL_NUTRITION_INTERRUPT,
      id: FragmentNames.ENTERAL_NUTRITION_INTERRUPT,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.ENTERAL_NUTRITION_INTERRUPT),
      title: interruptDescription,
      type: ModalType.DEFAULT,
    };
    AdminByProcessParams.admType = { isAdmProcess: false };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleMountActionTerminate(): void {
    this.actionTerminateApi.setEnabled(this.shouldShowTerminateAction());
  }

  @OnMount(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleMountActionAdjustSpeed({ target }: EventData<ActionAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);

    target.setEnabled(isValidStatus);
  }

  @OnClick(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleClickAdjustSpeedAction(): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const enteralNutrition = this.context.getBulkData() as unknown as EnteralNutritionType

    InfusionsViewerParams.adminSchedule = {
      dosageTypeDescription: enteralNutrition.dosageTypeDescription
    };
    InfusionsViewerParams.infusionDetailsParams = {
      ...InfusionsViewerParams.infusionDetailsParams,
      isTypeDosage: enteralNutrition.dosageType,
      itemType: PrescriptionType.ENTERAL_NUTRITION
    }
    InfusionsViewerParams.params = {
      currentSpeedInfusion: enteralNutrition.infusionSpeed,
      establishmentCode: MedicineAndSolutionsParams.params.nrGroupingId,
      infusionName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(prescriptionNumber),
      solutionNumber: String(sequenceNumber),
    }

    applicationContext.redirect(ApplicationRoute.ADEP_ADJUST_INFUSION_SPEED, {
      encounterId,
      patientId,
    });
  }

  @OnMount(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleMountActionReverse({ target }: EventData<ActionAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const param6 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSING_TIME_MANAGEMENT);
    const isValidStatus = param6 !== YesNoType.NO && InfusionItemStatus.NOT_STARTED !== itemStatus;
    target.setEnabled(isValidStatus);
  }

  @OnClick(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleClickReverseAction(): void {
    const { patientId, prescriptionNumber, encounterId, solutionNumber } = this.historyService.getCurrentRouteParams();
    const itemType = PrescriptionType.ENTERAL_NUTRITION;
    const solutionType = 2;
    applicationContext.redirect(ApplicationRoute.ADEP_REVERSE_ENTERAL_NUTRITION, { encounterId, itemType, patientId, prescriptionNumber, solutionNumber, solutionType });
  }

  @OnMount(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_RESCHEDULE)
  handleRescheduleActionMount(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isAllowedToReschedule());
  }

  @OnClick(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_RESCHEDULE)
  handleRescheduleActionClick(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  private isAllowedToReschedule(): boolean {
    const param29 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);
    return YesNoType.YES === param29 && this.isStatusPending();
  }

  private isStatusPending(): boolean {
    const { status } = TimelineContext.get();
    return [ItemStatus.PENDING, MedicationStatus.PENDING as string].includes(status)
  }

  @OnMount(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleMountSuspendAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isAllowedToSuspend());
  }

  @OnClick(EnteralNutritionViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleClickSuspendAction(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  private isAllowedToSuspend(): boolean {
    const allowSuspendAndRevert = 'S';
    const allowSuspend = 'U';
    const param4 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_NON_MEDICAL_SUSPEND_OR_REVERSE_SUSPENSIONS);
    
    return this.isStatusPending() && (param4 === allowSuspend || param4 === allowSuspendAndRevert);
  }

  @OnMount(EnteralNutritionViewerAliases.ENTERAL_NUTRITION_VIEWER_DECK)
  handleMountDeck(): void {
    const enteralNutrition = this.context.getBulkData() as unknown as EnteralNutritionType
    this.deckApi.setData([{ ...enteralNutrition, prescriptionPosition: PrescriptionPosition.LEFT, statusItem: MedicineAndSolutionsParams.params.ieStatus, }]);
  }

  private validateAndSetButtonVisibility(): void {
    const { hourStatus: enteralNutritionHourStatus, itemStatus } = this.historyService.getCurrentRouteParams() as { hourStatus: string, itemStatus: string };
    const isValidItemStatusForAdministered = [InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.T].some(status => status === itemStatus);
    const isNotStarted = enteralNutritionHourStatus === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.RESTARTED

    if (this.shouldShowTerminateAction()) {
      this.actionTerminateApi.setEnabled(true);
    }

    if ((enteralNutritionHourStatus === InfusionHourStatus.ADMINISTERED && isValidItemStatusForAdministered) || isNotStarted) {
      this.executeTimeButtonApi.setVisible(false);
      return;
    }

    if (this.shouldStartEnteralNutrition(itemStatus, enteralNutritionHourStatus)) {
      const label = this.setScreenLabel(ActionType.START_SOLUTION, enteralNutritionHourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    if (this.shouldRestartEnteralNutrition(itemStatus, enteralNutritionHourStatus)) {
      const label = this.setScreenLabel(ActionType.RESTART_SOLUTION, enteralNutritionHourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    this.executeTimeButtonApi.setVisible(false);
  }

  private shouldShowTerminateAction(): boolean {
    const param27 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR)
    if (param27 === YesNoType.YES) {
      return false
    }
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.T, InfusionItemStatus.II].some(st => st === itemStatus);
    const { pendingHours } = this.context.getBulkData()
    return isValidStatus && Number(pendingHours) === 1
  }

  private shouldStartEnteralNutrition(itemStatus: string, status: string): boolean {
    const isItemStatusValid = itemStatus === InfusionItemStatus.NOT_STARTED;
    const isStatusValid = status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION;
    const isPdoValid = this.isPdoValid();
    return isItemStatusValid && isStatusValid && isPdoValid
  }

  private shouldRestartEnteralNutrition(itemStatus: string, status: string): boolean {
    const isInterrupted = this.isInterrupted(itemStatus) && status === InfusionHourStatus.INTERRUPTED;
    const isPending = itemStatus === InfusionItemStatus.INTERRUPTED && status === InfusionHourStatus.PENDING;
    return isInterrupted || isPending;
  }

  private setScreenLabel(actionType: string, status: string): string {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isInterrupted = this.isInterrupted(itemStatus)

    if (
      actionType !== ActionType.START_SOLUTION ||
      (status === InfusionHourStatus.INTERRUPTED && isInterrupted) ||
      (status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.INTERRUPTED)
    ) {
      return 'button.infusion-viewer.restart'
    }

    if (status !== InfusionHourStatus.STARTED) {
      return 'text.start-solution-by-schedule'
    }

    return ''
  }

  private isInterrupted(itemStatus: string): boolean {
    return [InfusionItemStatus.II.valueOf(), InfusionItemStatus.INTERRUPTED.valueOf()].includes(itemStatus)
  }

  private isPdoValid(): boolean {
    const { isPdo } = this.context.getBulkData() as { isPdo: string };
    return isPdo ? isPdo === YesNoType.NO : true;
  }

  private setExecuteTimeButton(label: string): void {
    this.executeTimeButtonApi.setValue(MessageService.translate(label));
    this.executeTimeButtonApi.setVisible(true);
  }

  private fetchEnteralNutrition(): Promise<EnteralNutritionType> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    return HttpService.get<EnteralNutritionType>(Endpoint.EMAR_GET_ENTERAL_NUTRITION_INFORMATION, {
      scheduleSequenceNumber,
    }).then((response) => {
      const responseBody = response.body as EnteralNutritionType;
      this.context.set({ ...responseBody });
      return responseBody;
    })
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    const noCardsRead = AdminByScheduleParams.readParam?.noCardsRead;

    if (noCardsRead) {
      ModalService.close(id);
      ModalService.close('dialog-modal-service');
      return;
    }

    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }
}

export default EnteralNutritionController;
